import { useState, useEffect } from 'react';
import {
  AppBar, Toolbar, Button, IconButton, Stack, Drawer,
  Box, useMediaQuery, useTheme, Typography, List,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/Phone';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import StarIcon from '@mui/icons-material/Star';
import { NAV_LINKS } from '../data/constants';
import { LOGO_IMAGE } from '../assets/logoImage';
import { GOLD, GOLD2, GOLDF, GOLDG, DARK, NAVY, NAVY3 } from '../theme/theme';

function GlowLine({ bottom = false }) {
  return (
    <Box sx={{
      position: 'absolute',
      left: 0, right: 0,
      ...(bottom ? { bottom: 0 } : { top: 0 }),
      height: '2px',
      background: `linear-gradient(90deg, transparent 0%, ${GOLDF}66 15%, ${GOLDF} 40%, ${GOLDG} 50%, ${GOLDF} 60%, ${GOLDF}66 85%, transparent 100%)`,
      boxShadow: `0 0 14px ${GOLDF}, 0 0 30px ${GOLDF}55`,
    }} />
  );
}

function NavBtn({ label, active, onClick }) {
  return (
    <Button
      onClick={onClick}
      sx={{
        position: 'relative',
        color: active ? GOLDG : GOLDF,
        fontFamily: "'Cinzel', Georgia, serif",
        fontWeight: 700,
        fontSize: { md: '11px', lg: '12px' },
        letterSpacing: '0.13em',
        textTransform: 'uppercase',
        px: { md: 1.8, lg: 2.5 },
        py: 1.2,
        borderRadius: '10px',
        minWidth: 0,
        background: active
          ? `linear-gradient(160deg, rgba(30,24,4,0.98) 0%, rgba(10,8,2,0.98) 100%)`
          : `linear-gradient(160deg, rgba(18,16,4,0.97) 0%, rgba(6,5,1,0.97) 100%)`,
        border: active
          ? `1.5px solid ${GOLDG}`
          : `1.5px solid rgba(201,150,12,0.70)`,
        boxShadow: active
          ? `0 0 22px ${GOLDF}66, inset 0 0 12px rgba(201,150,12,0.08)`
          : `0 0 10px rgba(201,150,12,0.20), inset 0 0 6px rgba(201,150,12,0.04)`,
        transition: 'all 0.22s ease',
        '&:hover': {
          color: GOLDG,
          border: `1.5px solid ${GOLDG}`,
          boxShadow: `0 0 26px ${GOLDF}77, inset 0 0 14px rgba(201,150,12,0.10)`,
          transform: 'translateY(-1px)',
          background: `linear-gradient(160deg, rgba(30,24,4,0.99) 0%, rgba(10,8,2,0.99) 100%)`,
        },
      }}
    >
      {active && (
        <Box sx={{
          position: 'absolute', bottom: 0, left: '18%', right: '18%', height: '2px',
          background: `linear-gradient(90deg, transparent, ${GOLDG}, transparent)`,
          boxShadow: `0 0 8px ${GOLDF}`,
          borderRadius: '2px',
        }} />
      )}
      {label}
    </Button>
  );
}

export default function Navbar({ onEnquire }) {
  const [scrolled, setScrolled]     = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeLink, setActiveLink] = useState('Home');
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  const handleNav = (label) => {
    setActiveLink(label);
    setDrawerOpen(false);
    if (label === 'Home') { window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    const el = document.getElementById(label.toLowerCase());
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      {/* Trust Bar */}
      <Box sx={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1400,
        background: `linear-gradient(90deg, ${DARK} 0%, ${NAVY} 50%, ${DARK} 100%)`,
        borderBottom: `1px solid rgba(201,150,12,0.30)`,
        py: 0.7, px: { xs: 2, md: '5%' },
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={0.5}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: GOLD, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 0 6px ${GOLD}` }}>
                <Typography sx={{ fontSize: 8, color: '#000', fontWeight: 900 }}>✓</Typography>
              </Box>
              <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>DTCP &amp; RERA Approved Plots</Typography>
            </Box>
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.5 }}>
              <StarIcon sx={{ fontSize: 13, color: GOLD }} />
              <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>500+ Happy Customers</Typography>
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
              <LocationOnIcon sx={{ fontSize: 13, color: GOLD }} />
              <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>6 Prime Locations</Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.5 }}>
              <LocationOnIcon sx={{ fontSize: 12, color: 'rgba(255,255,255,0.40)' }} />
              <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.55)' }}>Coimbatore</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PhoneIcon sx={{ fontSize: 12, color: GOLDF, filter: `drop-shadow(0 0 4px ${GOLD})` }} />
              <Typography sx={{ fontSize: '11px', color: GOLDF, fontWeight: 700 }}>+91 80560 10228</Typography>
            </Box>
          </Stack>
        </Stack>
      </Box>

      {/* Main AppBar — styled like the image: dark warm-black with gold glowing strip */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          top: '33px',
          background: `linear-gradient(90deg,
            rgba(8,6,1,0.98) 0%,
            rgba(18,14,3,0.98) 20%,
            rgba(26,20,4,0.98) 50%,
            rgba(18,14,3,0.98) 80%,
            rgba(8,6,1,0.98) 100%
          )`,
          borderBottom: 'none',
          overflow: 'visible',
        }}
      >
        <Box sx={{ position: 'relative', height: '2px' }}><GlowLine /></Box>

        <Toolbar sx={{
          px: { xs: 2, md: '3%' },
          minHeight: { xs: 70, md: 80 },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}>
          {/* Logo */}
          <Box
            onClick={() => handleNav('Home')}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer',
              background: `linear-gradient(135deg, rgba(8,6,1,0.98) 0%, rgba(20,16,3,0.98) 100%)`,
              border: `1.5px solid ${GOLD}`,
              borderRadius: '12px', px: 1.8, py: 1,
              boxShadow: `0 0 20px rgba(201,150,12,0.35), inset 0 1px 0 rgba(255,210,60,0.10)`,
              '&:hover': { boxShadow: `0 0 32px rgba(201,150,12,0.55)` },
              transition: 'box-shadow 0.2s',
              flexShrink: 0,
            }}
          >
            <Box component="img" src={LOGO_IMAGE} alt="Elaris"
              sx={{ width: 44, height: 44, objectFit: 'cover', borderRadius: '7px', border: `1px solid ${GOLD}66` }} />
            <Box>
              <Typography sx={{ fontFamily: "'Cinzel', Georgia, serif", fontWeight: 700, fontSize: '17px', letterSpacing: '0.14em', color: GOLDF, lineHeight: 1.1, textShadow: `0 0 12px ${GOLD}88` }}>ELARIS</Typography>
              <Typography sx={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: '10px', color: 'rgba(245,197,24,0.65)', letterSpacing: '0.08em' }}>Enriching Elegance</Typography>
            </Box>
          </Box>

          {/* Desktop nav buttons */}
          {!isMobile && (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, justifyContent: 'center' }}>
              {NAV_LINKS.map((l) => (
                <NavBtn key={l} label={l} active={activeLink === l} onClick={() => handleNav(l)} />
              ))}
            </Stack>
          )}

          {/* Desktop CTAs */}
          {!isMobile && (
            <Stack direction="row" spacing={1.2} alignItems="center" sx={{ flexShrink: 0 }}>
              <Button
                component="a" href="tel:+918056010228"
                startIcon={<PhoneIcon sx={{ fontSize: 14 }} />}
                sx={{
                  color: GOLDF, fontWeight: 700, fontSize: '12px', px: 1.8, py: 1,
                  borderRadius: '10px', letterSpacing: '0.04em',
                  border: `1.5px solid rgba(201,150,12,0.55)`,
                  background: `linear-gradient(160deg, rgba(18,14,3,0.97) 0%, rgba(8,6,1,0.97) 100%)`,
                  '&:hover': { border: `1.5px solid ${GOLDF}`, boxShadow: `0 0 18px ${GOLDF}55` },
                }}>
                Call Us
              </Button>
              <Button
                onClick={onEnquire}
                startIcon={<CalendarMonthIcon sx={{ fontSize: 14 }} />}
                sx={{
                  background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD2} 100%)`,
                  color: DARK, fontWeight: 800, fontSize: '12px', px: 2, py: 1.1,
                  borderRadius: '10px', letterSpacing: '0.06em',
                  border: `1.5px solid ${GOLDF}`,
                  boxShadow: `0 0 22px ${GOLD}88, 0 4px 12px rgba(0,0,0,0.3)`,
                  '&:hover': { background: `linear-gradient(135deg, ${GOLD2} 0%, ${GOLDF} 100%)`, boxShadow: `0 0 30px ${GOLDF}99` },
                }}>
                Book Visit
              </Button>
            </Stack>
          )}

          {isMobile && (
            <Stack direction="row" gap={1} alignItems="center">
              <Button onClick={onEnquire} size="small" sx={{
                background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD2} 100%)`,
                color: DARK, fontWeight: 800, fontSize: '11px', px: 1.5, py: 0.8,
                borderRadius: '8px', border: `1px solid ${GOLDF}`, boxShadow: `0 0 12px ${GOLD}66`,
              }}>
                Book Visit
              </Button>
              <IconButton onClick={() => setDrawerOpen(true)} sx={{ color: GOLDF }}><MenuIcon /></IconButton>
            </Stack>
          )}
        </Toolbar>

        <Box sx={{ position: 'relative', height: '2px' }}><GlowLine bottom /></Box>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 280, background: `linear-gradient(160deg, rgba(8,6,1,0.99) 0%, rgba(20,16,3,0.99) 100%)`, borderLeft: `1px solid rgba(201,150,12,0.3)` } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: `1px solid rgba(201,150,12,0.2)` }}>
          <Typography sx={{ fontFamily: "'Cinzel', Georgia, serif", fontWeight: 700, color: GOLDF, letterSpacing: '0.12em' }}>ELARIS</Typography>
          <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: GOLDF }}><CloseIcon /></IconButton>
        </Box>
        <List sx={{ pt: 2, px: 2 }}>
          {NAV_LINKS.map((l) => (
            <Button key={l} fullWidth onClick={() => handleNav(l)} sx={{
              justifyContent: 'flex-start', py: 1.2,
              color: activeLink === l ? GOLDF : 'rgba(255,255,255,0.78)',
              fontFamily: "'Cinzel', Georgia, serif", fontWeight: 700,
              textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.10em',
              borderRadius: '10px', mb: 0.5,
              border: `1px solid ${activeLink === l ? GOLD : 'rgba(201,150,12,0.25)'}`,
              background: activeLink === l
                ? `linear-gradient(160deg, rgba(18,14,3,0.97) 0%, rgba(8,6,1,0.97) 100%)`
                : 'transparent',
              '&:hover': { bgcolor: 'rgba(201,150,12,0.08)', color: GOLDF, border: `1px solid ${GOLD}` },
            }}>{l}</Button>
          ))}
        </List>
        <Box sx={{ p: 2, mt: 'auto' }}>
          <Button fullWidth onClick={() => { setDrawerOpen(false); onEnquire(); }} sx={{
            background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD2} 100%)`,
            color: DARK, fontWeight: 800, py: 1.3, borderRadius: '10px',
            border: `1px solid ${GOLDF}`, boxShadow: `0 0 16px ${GOLD}66`,
          }}>
            Book Free Site Visit
          </Button>
        </Box>
      </Drawer>
    </>
  );
}
